'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';

export type SessionStatus = 'connecting' | 'streaming' | 'paused' | 'stopped';

export interface CognitiveMetrics {
  cognitiveLoadIndex: number;
  attentionScore: number;
  signalQualityIndex: number;
  artifactRate: number;
  thetaAlphaRatio: number;
  interHemisphericCoherence: number;
  deltaPower: number;
  thetaPower: number;
  alphaPower: number;
  betaPower: number;
  gammaPower: number;
}

export interface WaveformDataPoint {
  t: number;
  ch1: number;
  ch2: number;
  ch3: number;
  ch4: number;
  ch5: number;
  ch6: number;
  ch7: number;
  ch8: number;
}

export interface BandPowerPoint {
  t: number;
  delta: number;
  theta: number;
  alpha: number;
  beta: number;
}

export interface ArtifactEvent {
  id: string;
  timestamp: string;
  type: 'muscle' | 'eye-blink' | 'electrode-pop' | 'movement' | 'powerline';
  channel: string;
  severity: 'low' | 'medium' | 'high';
  amplitude: number;
}

export interface ChannelInfo {
  id: string;
  label: string;
  location: string;
  sqi: number;
  impedance: number;
  status: 'good' | 'fair' | 'poor' | 'disconnected';
  amplitude: number;
}

export interface SessionContextType {
  status: SessionStatus;
  metrics: CognitiveMetrics;
  waveformHistory: WaveformDataPoint[];
  bandPowerHistory: BandPowerPoint[];
  artifactEvents: ArtifactEvent[];
  channels: ChannelInfo[];
  sessionDuration: number;
  sampleRate: number;
  subjectId: string;
  sessionId: string;
  togglePause: () => void;
  stopSession: () => void;
  restartSession: () => void;
}

const SessionContext = createContext<SessionContextType | null>(null);

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}

// ─── Mock Data Generators ──────────────────────────────────────────────────
// Backend integration point: replace these generators with API calls to
// your EEG streaming endpoint (e.g. WebSocket ws://api.cognidash.io/stream)
// and Supabase real-time subscriptions (supabase.channel('eeg_sessions'))

function clamp(val: number, min: number, max: number) {
  return Math.min(max, Math.max(min, val));
}

function jitter(base: number, range: number) {
  return base + (Math.random() - 0.5) * 2 * range;
}

function generateEEGSample(
  prev: CognitiveMetrics,
  tick: number
): CognitiveMetrics {
  const phaseLoad = Math.sin(tick * 0.04) * 12 + Math.sin(tick * 0.13) * 6;
  const phaseAttn = Math.cos(tick * 0.05) * 10 + Math.sin(tick * 0.09) * 5;

  return {
    cognitiveLoadIndex: clamp(jitter(prev.cognitiveLoadIndex + phaseLoad * 0.15, 2.5), 18, 94),
    attentionScore: clamp(jitter(prev.attentionScore + phaseAttn * 0.12, 2), 22, 96),
    signalQualityIndex: clamp(jitter(prev.signalQualityIndex, 1.2), 70, 99),
    artifactRate: clamp(jitter(prev.artifactRate, 0.3), 0.1, 4.8),
    thetaAlphaRatio: clamp(jitter(prev.thetaAlphaRatio, 0.05), 0.4, 2.8),
    interHemisphericCoherence: clamp(jitter(prev.interHemisphericCoherence, 1.5), 45, 95),
    deltaPower: clamp(jitter(prev.deltaPower, 1.8), 4, 28),
    thetaPower: clamp(jitter(prev.thetaPower, 2.1), 6, 42),
    alphaPower: clamp(jitter(prev.alphaPower, 1.9), 8, 38),
    betaPower: clamp(jitter(prev.betaPower, 2.4), 5, 35),
    gammaPower: clamp(jitter(prev.gammaPower, 1.2), 2, 18),
  };
}

function generateWaveformPoint(tick: number): WaveformDataPoint {
  const base = (t: number, freq: number, amp: number) =>
    Math.sin(t * freq) * amp + (Math.random() - 0.5) * 4;
  return {
    t: tick,
    ch1: base(tick, 0.8, 18) + base(tick, 3.2, 8),
    ch2: base(tick, 0.7, 22) + base(tick, 2.8, 6),
    ch3: base(tick, 1.1, 15) + base(tick, 4.1, 5),
    ch4: base(tick, 0.6, 20) + base(tick, 3.5, 7),
    ch5: base(tick, 0.9, 16) + base(tick, 2.6, 9),
    ch6: base(tick, 1.3, 14) + base(tick, 3.8, 4),
    ch7: base(tick, 0.5, 24) + base(tick, 2.4, 10),
    ch8: base(tick, 1.0, 17) + base(tick, 3.0, 6),
  };
}

function generateBandPoint(metrics: CognitiveMetrics, tick: number): BandPowerPoint {
  return {
    t: tick,
    delta: metrics.deltaPower,
    theta: metrics.thetaPower,
    alpha: metrics.alphaPower,
    beta: metrics.betaPower,
  };
}

function generateArtifactEvent(tick: number): ArtifactEvent | null {
  if (Math.random() > 0.12) return null;
  const types: ArtifactEvent['type'][] = ['muscle', 'eye-blink', 'electrode-pop', 'movement', 'powerline'];
  const severities: ArtifactEvent['severity'][] = ['low', 'medium', 'high'];
  const channels = ['Fp1', 'Fp2', 'F3', 'F4', 'C3', 'C4', 'P3', 'P4'];
  const type = types[Math.floor(Math.random() * types.length)];
  const severity = severities[Math.floor(Math.random() * (Math.random() > 0.7 ? 3 : 2))];
  const ch = channels[Math.floor(Math.random() * channels.length)];
  const now = new Date();
  return {
    id: `evt-${tick}-${Math.floor(Math.random() * 9999)}`,
    timestamp: now.toISOString(),
    type,
    channel: ch,
    severity,
    amplitude: Math.round(jitter(45, 35)),
  };
}

const INITIAL_METRICS: CognitiveMetrics = {
  cognitiveLoadIndex: 58,
  attentionScore: 71,
  signalQualityIndex: 92,
  artifactRate: 1.2,
  thetaAlphaRatio: 1.4,
  interHemisphericCoherence: 78,
  deltaPower: 12.4,
  thetaPower: 18.6,
  alphaPower: 22.1,
  betaPower: 15.8,
  gammaPower: 7.3,
};

const INITIAL_CHANNELS: ChannelInfo[] = [
  { id: 'ch-fp1', label: 'Fp1', location: 'Left Prefrontal', sqi: 94, impedance: 4.2, status: 'good', amplitude: 18.4 },
  { id: 'ch-fp2', label: 'Fp2', location: 'Right Prefrontal', sqi: 91, impedance: 5.1, status: 'good', amplitude: 17.9 },
  { id: 'ch-f3', label: 'F3', location: 'Left Frontal', sqi: 88, impedance: 6.8, status: 'good', amplitude: 20.2 },
  { id: 'ch-f4', label: 'F4', location: 'Right Frontal', sqi: 85, impedance: 7.4, status: 'fair', amplitude: 19.6 },
  { id: 'ch-c3', label: 'C3', location: 'Left Central', sqi: 96, impedance: 3.9, status: 'good', amplitude: 22.8 },
  { id: 'ch-c4', label: 'C4', location: 'Right Central', sqi: 93, impedance: 4.6, status: 'good', amplitude: 21.3 },
  { id: 'ch-p3', label: 'P3', location: 'Left Parietal', sqi: 62, impedance: 18.2, status: 'fair', amplitude: 15.7 },
  { id: 'ch-p4', label: 'P4', location: 'Right Parietal', sqi: 34, impedance: 42.6, status: 'poor', amplitude: 8.1 },
];

function seedWaveformHistory(): WaveformDataPoint[] {
  return Array.from({ length: 60 }, (_, i) => generateWaveformPoint(i));
}

function seedBandHistory(metrics: CognitiveMetrics): BandPowerPoint[] {
  return Array.from({ length: 30 }, (_, i) => generateBandPoint(metrics, i));
}

export default function SessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('connecting');
  const [metrics, setMetrics] = useState<CognitiveMetrics>(INITIAL_METRICS);
  const [waveformHistory, setWaveformHistory] = useState<WaveformDataPoint[]>(seedWaveformHistory);
  const [bandPowerHistory, setBandPowerHistory] = useState<BandPowerPoint[]>(() =>
    seedBandHistory(INITIAL_METRICS)
  );
  const [artifactEvents, setArtifactEvents] = useState<ArtifactEvent[]>([]);
  const [channels, setChannels] = useState<ChannelInfo[]>(INITIAL_CHANNELS);
  const [sessionDuration, setSessionDuration] = useState(0);
  const tickRef = useRef(60);
  const metricsRef = useRef(INITIAL_METRICS);

  // Simulate connection delay
  useEffect(() => {
    const t = setTimeout(() => setStatus('streaming'), 1400);
    return () => clearTimeout(t);
  }, []);

  // Main streaming loop — Backend integration point:
  // Replace this setInterval with a WebSocket message handler:
  // const ws = new WebSocket(process.env.NEXT_PUBLIC_EEG_WS_URL);
  // ws.onmessage = (e) => { const data = JSON.parse(e.data); updateMetrics(data); }
  // Also subscribe to Supabase for artifact events:
  // supabase.channel('artifact_events').on('postgres_changes', ...).subscribe()
  useEffect(() => {
    if (status !== 'streaming') return;
    const interval = setInterval(() => {
      tickRef.current += 1;
      const tick = tickRef.current;

      const newMetrics = generateEEGSample(metricsRef.current, tick);
      metricsRef.current = newMetrics;
      setMetrics({ ...newMetrics });

      const wavePoint = generateWaveformPoint(tick);
      setWaveformHistory((prev) => {
        const next = [...prev, wavePoint];
        return next.length > 80 ? next.slice(next.length - 80) : next;
      });

      const bandPoint = generateBandPoint(newMetrics, tick);
      setBandPowerHistory((prev) => {
        const next = [...prev, bandPoint];
        return next.length > 40 ? next.slice(next.length - 40) : next;
      });

      // Channel SQI drift
      setChannels((prev) =>
        prev.map((ch) => {
          const newSqi = clamp(jitter(ch.sqi, 0.8), ch.status === 'poor' ? 20 : 60, 99);
          const newImpedance = clamp(jitter(ch.impedance, 0.5), 2, 60);
          const newAmp = clamp(jitter(ch.amplitude, 0.6), 4, 30);
          return { ...ch, sqi: Math.round(newSqi), impedance: parseFloat(newImpedance.toFixed(1)), amplitude: parseFloat(newAmp.toFixed(1)) };
        })
      );

      const evt = generateArtifactEvent(tick);
      if (evt) {
        setArtifactEvents((prev) => [evt, ...prev].slice(0, 50));
      }

      setSessionDuration((d) => d + 1);
    }, 800);

    return () => clearInterval(interval);
  }, [status]);

  const togglePause = useCallback(() => {
    setStatus((s) => (s === 'streaming' ? 'paused' : 'streaming'));
  }, []);

  const stopSession = useCallback(() => {
    setStatus('stopped');
  }, []);

  const restartSession = useCallback(() => {
    tickRef.current = 0;
    metricsRef.current = INITIAL_METRICS;
    setMetrics(INITIAL_METRICS);
    setWaveformHistory(seedWaveformHistory());
    setBandPowerHistory(seedBandHistory(INITIAL_METRICS));
    setArtifactEvents([]);
    setChannels(INITIAL_CHANNELS);
    setSessionDuration(0);
    setStatus('connecting');
    setTimeout(() => setStatus('streaming'), 1400);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        status,
        metrics,
        waveformHistory,
        bandPowerHistory,
        artifactEvents,
        channels,
        sessionDuration,
        sampleRate: 256,
        subjectId: 'SBJ-2847',
        sessionId: 'SES-20260328-0041',
        togglePause,
        stopSession,
        restartSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}